import { DeleteIcon, ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  HStack,
  VStack,
  Text,
  Checkbox,
  Tag,
} from "@chakra-ui/react";
import { FormikErrors } from "formik";
import React, { useState } from "react";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { FieldType, FieldTypeList } from "../../enums/FieldType";
import {
  getFormFieldId,
  RegistrationFormConfigurationDetailFormValues,
} from "../../forms/RegistrationFormConfigurationDetailForm";
import { IntegerField } from "../fields/IntegerField/IntegerField";
import { ChakraReactSelectField } from "../fields/SelectField/ChakraReactSelectField";
import { SwitchField } from "../fields/SwitchField/SwitchField";
import { EditAsHtmlButton } from "../fields/TextArea/EditAsHtmlButton";
import { TextArea } from "../fields/TextArea/TextArea";
import { TextField } from "../fields/TextField/TextField";
import { TimePeriod } from "./RegistrationFormTimePeriod";
import { Button, IconButton } from "@chakra-ui/button";
import { PlusIcon } from "../../styles/icons";
import { IntegrationObjectField } from "./RegistrationFormIntegrationObjectField";
import { KeyValuePairIntegrationSection } from "./RegistrationFormKeyValuePairIntegrationSection";
import { ThirdPartyDataConfigSection } from "./RegistrationFormThirdPartyDataConfigSection";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { AdminApiEnumConfigurations } from "../../enums/AdminApiEnumConfigurations";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableItem } from "../Dnd/SortableItem";
import { useCheckboxState } from "../../hooks/useCheckboxState";
import { VisibleField } from "../../enums/VisibleField";
import { isExtendedField } from "../../utils/extendedFieldUtils";
import { stripHtmlToPlainText } from "../../util/stringHelper";

export type CustomFormField = {
  Identifier: string;
  Name: string;
  Intro?: string | null;
  Description?: string | null;
  HideFieldName?: boolean;
  IsMultiSelect?: boolean;
  IsStaffOnly?: boolean;
  IsRequired?: boolean;
  Type: FieldType;
  TextboxPlaceholderValue?: string | null;
  KeyValuePairs?: { Key: string; Value: string }[] | null;
  DefaultSelectedValue?: string | null;
  Value?: string | null;
  ValueLabel?: string | null;
  IntegrationObject?: { key: string; value: unknown }[] | null;
  IntegrationObjectNotRequired?: boolean;
  UnsetValue?: string | null;
  SortKeyValuePairsByValue?: boolean;
  DateSelectedMinRelativeTimePeriod?: CherryHubAdminApi.TimePeriod;
  DateSelectedMaxRelativeTimePeriod?: CherryHubAdminApi.TimePeriod;
  DateSelectedMinValue?: string | null;
  DateSelectedMaxValue?: string | null;
  FeeAmounts?:
    | {
        key: string;
        value: CherryHubAdminApi.FeeDetail;
      }[]
    | null;
  FieldValidationRegexType?: string;
  CustomValidationRegexPattern?: string | null;
  FieldSpecification?: CherryHubAdminApi.FieldSpecification;
  IsVisible?: boolean;
  AcceptThirdPartyData?: boolean;
  ThirdPartyDataFieldDataletConfigurations?:
    | {
        ThirdPartyIdentifier?: string | null;
        JsonPath?: string | null;
        OverrideExistingFieldDataletValue?: boolean;
        AddToExistingFieldDataletValue?: boolean;
        IsRequired?: boolean;
        IsReadOnlyForPatron?: boolean;
      }[]
    | null;
};

interface CustomFieldSectionProps {
  customFormFields: CustomFormField[] | null | undefined;
  addCustomFormField: (scrollTo?: boolean) => void;
  addKeyValuePairs: (formField: CustomFormField) => void;
  validateKeyValuePairs: (formField: CustomFormField) => Promise<string[]>;
  replaceAllKeyValuePairs: (formField: CustomFormField) => void;
  removeCustomFormField: (formFielIdx: number) => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  twoStepFlowRequired?: boolean;
  validateFormFields: () => (JSX.Element | undefined)[] | undefined;
  errors: FormikErrors<RegistrationFormConfigurationDetailFormValues>;
}

export const CustomFieldSection = ({
  customFormFields,
  addCustomFormField,
  addKeyValuePairs,
  validateKeyValuePairs,
  replaceAllKeyValuePairs,
  removeCustomFormField,
  setFieldValue,
  validateFormFields,
  errors,
  twoStepFlowRequired,
}: CustomFieldSectionProps) => {
  const hasMemberPhotoField = customFormFields?.some(
    (f) => f.Type === FieldType.MemberPhotoUpload
  );

  const fieldTypeOptions = FieldTypeList();

  const [activeId, setActiveId] = useState<{
    idx: number;
    customField?: CustomFormField | null;
  } | null>(null);

  const {
    data: fieldValidationRegexTypeOptions,
    isLoading: fieldValidationRegexTypeOptionsLoading,
  } = useAdminApiRequest(
    (apiClient) =>
      apiClient.getEnumConfigurations(
        AdminApiEnumConfigurations.FieldValidationRegexType
      ),
    []
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event: any) => {
    const { active } = event;
    const customFieldIdx =
      customFormFields?.findIndex((df) => getFormFieldId(df) == active.id) ??
      -1;
    setActiveId({
      idx: customFieldIdx,
      customField: customFormFields?.[customFieldIdx] ?? null,
    });
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (!customFormFields) {
      return setActiveId(null);
    }

    if (!over || !over.id) {
      return setActiveId(null);
    }

    if (active.id == over.id) {
      return setActiveId(null);
    }

    const oldIndex = customFormFields.findIndex(
      (item) => getFormFieldId(item) == active.id
    );
    const newIndex = customFormFields.findIndex(
      (item) => getFormFieldId(item) == over.id
    );
    setFieldValue(
      "formFields",
      arrayMove(customFormFields, oldIndex, newIndex)
    );
    setActiveId(null);
  };

  const {
    checked: showDeletedCustomFields,
    onChange: onChangeDeletedCustomFieldsCheck,
  } = useCheckboxState(false);

  const applyVisibility = (
    fieldName: string,
    spec?: CherryHubAdminApi.FieldSpecification
  ) => {
    return !(spec?.VisibleFields?.find(
      (field) => field.toLowerCase() == fieldName.toLowerCase()
    )
      ? true
      : false);
  };

  const addKeyValuePair = (
    formField: CustomFormField,
    formFieldIdx: number
  ) => {
    if (
      formField?.FieldSpecification?.IsNonIntegratedField ||
      formField.FieldSpecification?.IsCustomIntegrationField ||
      !formField.FieldSpecification?.IsDataListAvailable
    ) {
      if (formField.KeyValuePairs) {
        formField.KeyValuePairs?.push({
          Key: "",
          Value: "",
        });
        setFieldValue(
          `formFields[${formFieldIdx}].KeyValuePairs`,
          formField.KeyValuePairs
        );
      } else {
        setFieldValue(`formFields[${formFieldIdx}].KeyValuePairs`, [
          { key: "", value: "" },
        ]);
      }
    } else {
      addKeyValuePairs(formField);
    }
  };

  return (
    <>
      {(customFormFields?.length ?? 0) > 0 && (
        <HStack w="100%" pt={4}>
          <Box flex="1">
            <Checkbox
              checked={showDeletedCustomFields}
              onChange={onChangeDeletedCustomFieldsCheck}
              title="Show all custom form fields in the list."
            >
              Show Hidden
            </Checkbox>
          </Box>
          <Button
            leftIcon={<PlusIcon />}
            onClick={() => addCustomFormField(true)}
            colorScheme="cherryButton"
            alignSelf="flex-end"
            size="sm"
          >
            Form Field
          </Button>
        </HStack>
      )}
      {validateFormFields()}
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Accordion w="100%" allowToggle variant="custom-field">
          <VStack w="100%">
            <SortableContext
              items={customFormFields?.map((cf) => getFormFieldId(cf)) ?? []}
              strategy={verticalListSortingStrategy}
            >
              {(customFormFields?.length &&
                customFormFields.map((formField, formFieldIdx) => {
                  if (showDeletedCustomFields ? true : formField.IsVisible)
                    return (
                      <AccordionItem
                        w="100%"
                        key={getFormFieldId(formField)}
                        className={`custom-field-${formFieldIdx}`}
                        border={
                          formField.IsVisible
                            ? "1px solid var(--chakra-colors-cherryGrey-300)"
                            : "1px solid var(--chakra-colors-cherryGreyAlpha-300)"
                        }
                        bgColor={
                          formField.IsVisible
                            ? "cherryGrey.40"
                            : "cherryGreyAlpha.40"
                        }
                      >
                        {({ isExpanded }) => (
                          <>
                            <HStack w="100%" pl={4}>
                              <SortableItem
                                id={getFormFieldId(formField)}
                                whiteSpace="nowrap"
                              >
                                <AccordionButton
                                  style={{
                                    paddingLeft: "unset",
                                    paddingRight: "unset",
                                  }}
                                  _focus={{ boxShadow: "unset" }}
                                >
                                  {!formField.IsVisible && (
                                    <Tag size="sm" mr="2">
                                      Hidden
                                    </Tag>
                                  )}
                                  <Text opacity={formField.IsVisible ? 1 : 0.4}>
                                    <b>
                                      {stripHtmlToPlainText(formField.Name) ||
                                        `#${formFieldIdx + 1} Custom Field`}
                                    </b>{" "}
                                    - {getFormFieldId(formField)}
                                  </Text>
                                  <Text
                                    as="span"
                                    color="red"
                                    fontSize="md"
                                    opacity={formField.IsVisible ? 1 : 0.4}
                                  >
                                    {formField.FieldSpecification
                                      ?.IsSystemRequired && "*"}
                                  </Text>
                                </AccordionButton>
                              </SortableItem>

                              {formField.IsVisible && (
                                <IconButton
                                  aria-label="Hide custom form field."
                                  title="Hide custom form field."
                                  icon={<ViewOffIcon />}
                                  size="xs"
                                  bgColor="cherryGrey.160"
                                  onClick={() =>
                                    setFieldValue(
                                      `formFields[${formFieldIdx}].IsVisible`,
                                      false
                                    )
                                  }
                                />
                              )}
                              {!formField.IsVisible && (
                                <IconButton
                                  aria-label="Show custom form field."
                                  title="Show custom form field."
                                  icon={<ViewIcon />}
                                  size="xs"
                                  bgColor="cherryGrey.160"
                                  onClick={() =>
                                    setFieldValue(
                                      `formFields[${formFieldIdx}].IsVisible`,
                                      true
                                    )
                                  }
                                />
                              )}

                              <IconButton
                                aria-label="Delete custom form field."
                                title="Delete custom form field."
                                icon={<DeleteIcon />}
                                size="xs"
                                bgColor="cherryGrey.160"
                                onClick={() =>
                                  removeCustomFormField(formFieldIdx)
                                }
                              />

                              <AccordionButton
                                flexGrow={1}
                                justifyContent="flex-end"
                                _focus={{ boxShadow: "unset" }}
                                opacity={formField.IsVisible ? 1 : 0.4}
                              >
                                <AccordionIcon />
                              </AccordionButton>
                            </HStack>
                            <AccordionPanel
                              width="100%"
                              opacity={formField.IsVisible ? 1 : 0.4}
                            >
                              {isExpanded && (
                                <VStack w="100%" alignItems="flex-start">
                                  <HStack w="100%" spacing={4}>
                                    <TextField
                                      horizontal
                                      label="Name"
                                      name={`formFields[${formFieldIdx}].Name`}
                                      hideField={applyVisibility(
                                        VisibleField.Name,
                                        formField.FieldSpecification
                                      )}
                                      fcProps={{ maxW: "32.6%" }}
                                      labelRightElement={
                                        <EditAsHtmlButton
                                          name={`formFields[${formFieldIdx}].Name`}
                                          fieldLabel="Name"
                                        />
                                      }
                                    />
                                    <ChakraReactSelectField
                                      horizontal
                                      label="Type"
                                      name={`formFields[${formFieldIdx}].Type`}
                                      selectedOptionColor="blue"
                                      options={fieldTypeOptions.filter(
                                        (option) =>
                                          option.value !==
                                            FieldType.MemberPhotoUpload ||
                                          formField.Type ===
                                            FieldType.MemberPhotoUpload ||
                                          !hasMemberPhotoField
                                      )}
                                      isDisabled={
                                        formField.FieldSpecification
                                          ?.IsNonIntegratedField ||
                                        formField.FieldSpecification
                                          ?.IsCustomIntegrationField
                                          ? false
                                          : true
                                      }
                                      hideField={applyVisibility(
                                        VisibleField.Type,
                                        formField.FieldSpecification
                                      )}
                                      fcProps={{ maxW: "32.6%" }}
                                    />
                                  </HStack>

                                  <HStack w="100%" spacing={4}>
                                    <TextField
                                      horizontal
                                      label="Value"
                                      name={`formFields[${formFieldIdx}].Value`}
                                      hideField={applyVisibility(
                                        VisibleField.Value,
                                        formField.FieldSpecification
                                      )}
                                    />
                                    <TextField
                                      horizontal
                                      label="Default Selected Value"
                                      name={`formFields[${formFieldIdx}].DefaultSelectedValue`}
                                      isDisabled={isExtendedField(
                                        formField.Identifier
                                      )}
                                    />

                                    <TextField
                                      horizontal
                                      label="Unset Value"
                                      name={`formFields[${formFieldIdx}].UnsetValue`}
                                      hideField={applyVisibility(
                                        VisibleField.UnsetValue,
                                        formField.FieldSpecification
                                      )}
                                    />
                                  </HStack>

                                  <HStack w="100%" spacing={4}>
                                    <TextField
                                      horizontal
                                      label="Textbox Placeholder Value"
                                      name={`formFields[${formFieldIdx}].TextboxPlaceholderValue`}
                                      hideField={applyVisibility(
                                        VisibleField.TextboxPlaceholderValue,
                                        formField.FieldSpecification
                                      )}
                                      fcProps={{ maxW: "32.6%" }}
                                    />

                                    <TextField
                                      horizontal
                                      label="Value Label"
                                      name={`formFields[${formFieldIdx}].ValueLabel`}
                                      hideField={applyVisibility(
                                        VisibleField.ValueLabel,
                                        formField.FieldSpecification
                                      )}
                                      fcProps={{ maxW: "32.6%" }}
                                    />
                                  </HStack>

                                  <TextArea
                                    horizontal
                                    valueProps={{ flex: 11 }}
                                    label="Intro"
                                    name={`formFields[${formFieldIdx}].Intro`}
                                    hideField={applyVisibility(
                                      VisibleField.Intro,
                                      formField.FieldSpecification
                                    )}
                                    labelRightElement={
                                      <EditAsHtmlButton
                                        name={`formFields[${formFieldIdx}].Intro`}
                                        fieldLabel="Intro"
                                      />
                                    }
                                  />

                                  <TextArea
                                    horizontal
                                    valueProps={{ flex: 11 }}
                                    label="Description"
                                    name={`formFields[${formFieldIdx}].Description`}
                                    hideField={applyVisibility(
                                      VisibleField.Description,
                                      formField.FieldSpecification
                                    )}
                                    labelRightElement={
                                      <EditAsHtmlButton
                                        name={`formFields[${formFieldIdx}].Description`}
                                        fieldLabel="Description"
                                      />
                                    }
                                  />

                                  <HStack w="100%" spacing={4}>
                                    <SwitchField
                                      horizontal
                                      labelProps={{
                                        flexShrink: 1,
                                        whiteSpace: "nowrap",
                                      }}
                                      valueProps={{ flexGrow: 5 }}
                                      label="Hide Field Name"
                                      name={`formFields[${formFieldIdx}].HideFieldName`}
                                      colorScheme="green"
                                      hideField={applyVisibility(
                                        VisibleField.HideFieldName,
                                        formField.FieldSpecification
                                      )}
                                    />

                                    <SwitchField
                                      horizontal
                                      labelProps={{
                                        flexShrink: 1,
                                        whiteSpace: "nowrap",
                                      }}
                                      valueProps={{ flexGrow: 5 }}
                                      label="Is Multi Select"
                                      name={`formFields[${formFieldIdx}].IsMultiSelect`}
                                      colorScheme="green"
                                      hideField={
                                        formField.Type !=
                                          FieldType.ListboxGroup ||
                                        applyVisibility(
                                          VisibleField.IsMultiSelect,
                                          formField.FieldSpecification
                                        )
                                      }
                                      isDisabled={isExtendedField(
                                        formField.Identifier
                                      )}
                                    />

                                    <SwitchField
                                      horizontal
                                      labelProps={{
                                        flexShrink: 1,
                                        whiteSpace: "nowrap",
                                      }}
                                      valueProps={{ flexGrow: 5 }}
                                      label="Is Required"
                                      name={`formFields[${formFieldIdx}].IsRequired`}
                                      colorScheme="green"
                                      hideField={applyVisibility(
                                        VisibleField.IsRequired,
                                        formField.FieldSpecification
                                      )}
                                      isDisabled={
                                        formField.FieldSpecification
                                          ?.IsSystemRequired
                                      }
                                    />

                                    <SwitchField
                                      horizontal
                                      labelProps={{
                                        flexShrink: 1,
                                        whiteSpace: "nowrap",
                                      }}
                                      valueProps={{ flexGrow: 5 }}
                                      label="Is Staff Only"
                                      name={`formFields[${formFieldIdx}].IsStaffOnly`}
                                      colorScheme="green"
                                    />
                                  </HStack>

                                  {!applyVisibility(
                                    VisibleField.IntegrationObject,
                                    formField.FieldSpecification
                                  ) &&
                                    !formField.FieldSpecification
                                      ?.IsNonIntegratedField && (
                                      <IntegrationObjectField
                                        headerLabel="Integration Object"
                                        fieldName={`formFields[${formFieldIdx}].IntegrationObject`}
                                        keyValuePairs={
                                          formField.IntegrationObject
                                        }
                                        editableKeys={
                                          formField.FieldSpecification
                                            ?.IsCustomIntegrationField
                                            ? formField.FieldSpecification
                                                ?.EditableIntegrationObjectValues
                                            : null
                                        }
                                        setFieldValue={setFieldValue}
                                        borderColor="cherryButtonDark.100"
                                        errors={errors}
                                      />
                                    )}

                                  <SwitchField
                                    horizontal
                                    label="Sort Key Value Pairs By Value"
                                    labelProps={{
                                      flexShrink: 1,
                                      whiteSpace: "nowrap",
                                    }}
                                    valueProps={{ flexGrow: 5 }}
                                    fcProps={{ maxW: "20%" }}
                                    name={`formFields[${formFieldIdx}].SortKeyValuePairsByValue`}
                                    hideField={
                                      formField.FieldSpecification
                                        ?.IsNonIntegratedField ||
                                      formField.FieldSpecification
                                        ?.IsCustomIntegrationField
                                        ? !(
                                            formField.Type ==
                                            FieldType.ListboxGroup
                                          )
                                        : applyVisibility(
                                            VisibleField.SortKeyValuePairsByValue,
                                            formField.FieldSpecification
                                          )
                                    }
                                  />

                                  {(formField.FieldSpecification
                                    ?.IsNonIntegratedField ||
                                  formField.FieldSpecification
                                    ?.IsCustomIntegrationField
                                    ? formField.Type == FieldType.ListboxGroup
                                    : true) &&
                                    !applyVisibility(
                                      VisibleField.KeyValuePairs,
                                      formField.FieldSpecification
                                    ) && (
                                      <KeyValuePairIntegrationSection
                                        headerLabel="Key Value Pairs"
                                        fieldName={`formFields[${formFieldIdx}].KeyValuePairs`}
                                        keyValuePairs={formField.KeyValuePairs}
                                        addKeyValuePair={() =>
                                          addKeyValuePair(
                                            formField,
                                            formFieldIdx
                                          )
                                        }
                                        validateKeyValuePairs={() =>
                                          validateKeyValuePairs(formField)
                                        }
                                        replaceAllKeyValuePairs={() =>
                                          replaceAllKeyValuePairs(formField)
                                        }
                                        setFieldValue={setFieldValue}
                                        disableKeyField={
                                          (!formField.FieldSpecification
                                            ?.IsNonIntegratedField &&
                                            !formField.FieldSpecification
                                              ?.IsCustomIntegrationField &&
                                            formField.FieldSpecification
                                              ?.IsDataListAvailable) ||
                                          isExtendedField(formField.Identifier)
                                        }
                                        isNonIntegratedField={
                                          formField.FieldSpecification
                                            ?.IsNonIntegratedField ||
                                          formField.FieldSpecification
                                            ?.IsCustomIntegrationField
                                        }
                                        isExtendedField={isExtendedField(
                                          formField.Identifier
                                        )}
                                        borderColor="cherryButtonDark.100"
                                      />
                                    )}

                                  {formField.Type == FieldType.Date && (
                                    <>
                                      <HStack w="100%" alignItems="flex-start">
                                        {!applyVisibility(
                                          VisibleField.DateSelectedMinRelativeTimePeriod,
                                          formField.FieldSpecification
                                        ) && (
                                          <TimePeriod
                                            headerLabel="Date Selected Min Relative Time Period"
                                            fieldName={`formFields[${formFieldIdx}].DateSelectedMinRelativeTimePeriod`}
                                            borderColor="cherryButtonDark.100"
                                            timePeriod={
                                              formField.DateSelectedMinRelativeTimePeriod
                                            }
                                            isRelativeListbox
                                            isDisabled={isExtendedField(
                                              formField.Identifier
                                            )}
                                          />
                                        )}
                                        {!applyVisibility(
                                          VisibleField.DateSelectedMaxRelativeTimePeriod,
                                          formField.FieldSpecification
                                        ) && (
                                          <TimePeriod
                                            headerLabel="Date Selected Max Relative Time Period"
                                            fieldName={`formFields[${formFieldIdx}].DateSelectedMaxRelativeTimePeriod`}
                                            borderColor="cherryButtonDark.100"
                                            timePeriod={
                                              formField.DateSelectedMaxRelativeTimePeriod
                                            }
                                            isRelativeListbox
                                            isDisabled={isExtendedField(
                                              formField.Identifier
                                            )}
                                          />
                                        )}
                                      </HStack>
                                      <HStack w="100%" spacing={4}>
                                        <TextField
                                          horizontal
                                          label="Date Selected Min Value"
                                          name={`formFields[${formFieldIdx}].DateSelectedMinValue`}
                                          type="datetime-local"
                                          isDisabled={isExtendedField(
                                            formField.Identifier
                                          )}
                                        />
                                        <TextField
                                          horizontal
                                          label="Date Selected Max Value"
                                          name={`formFields[${formFieldIdx}].DateSelectedMaxValue`}
                                          type="datetime-local"
                                          isDisabled={isExtendedField(
                                            formField.Identifier
                                          )}
                                        />
                                      </HStack>
                                    </>
                                  )}

                                  {(formField.FieldSpecification
                                    ?.IsNonIntegratedField ||
                                  formField.FieldSpecification
                                    ?.IsCustomIntegrationField
                                    ? formField.Type == FieldType.Checkbox ||
                                      formField.Type == FieldType.ListboxGroup
                                    : !applyVisibility(
                                        VisibleField.FeeAmounts,
                                        formField.FieldSpecification
                                      )) && (
                                    <Box
                                      w="100%"
                                      p={4}
                                      border="1px solid"
                                      borderColor="cherryButtonDark.100"
                                      borderRadius="md"
                                    >
                                      <HStack w="100%">
                                        <Text flexGrow={2} fontSize="sm">
                                          <b>Fee Amounts</b> :
                                        </Text>
                                        <Button
                                          size="xs"
                                          leftIcon={<PlusIcon />}
                                          onClick={() => {
                                            if (formField.FeeAmounts) {
                                              formField.FeeAmounts?.push({
                                                key: "",
                                                value: {},
                                              });
                                              setFieldValue(
                                                `formFields[${formFieldIdx}].FeeAmounts`,
                                                formField.FeeAmounts
                                              );
                                            } else {
                                              setFieldValue(
                                                `formFields[${formFieldIdx}].FeeAmounts`,
                                                [{ key: "", value: {} }]
                                              );
                                            }
                                          }}
                                          colorScheme="cherryButton"
                                        >
                                          New Fee Amounts
                                        </Button>
                                      </HStack>

                                      <Divider mb={2} mt={1} />
                                      <VStack w="100%">
                                        {(formField.FeeAmounts?.length &&
                                          formField.FeeAmounts?.map(
                                            (kvp, kvpIdx) => {
                                              return (
                                                <VStack w="100%" key={kvpIdx}>
                                                  <HStack w="100%" spacing={4}>
                                                    <TextField
                                                      horizontal
                                                      labelProps={{
                                                        whiteSpace: "nowrap",
                                                      }}
                                                      valueProps={{
                                                        flexGrow: 10,
                                                      }}
                                                      label={`Key ${
                                                        kvpIdx + 1
                                                      }`}
                                                      name={`formFields[${formFieldIdx}].FeeAmounts[${kvpIdx}].key`}
                                                    />
                                                    <IntegerField
                                                      horizontal
                                                      labelProps={{
                                                        whiteSpace: "nowrap",
                                                      }}
                                                      valueProps={{
                                                        flexGrow: 10,
                                                      }}
                                                      label={`Fee Amount ${
                                                        kvpIdx + 1
                                                      }`}
                                                      name={`formFields[${formFieldIdx}].FeeAmounts[${kvpIdx}].value.FeeAmount`}
                                                    />

                                                    <Box
                                                      h="100%"
                                                      pb={1}
                                                      alignSelf="flex-end"
                                                    >
                                                      <IconButton
                                                        aria-label="Delete fee amounts."
                                                        title="Delete fee amounts."
                                                        icon={<DeleteIcon />}
                                                        size="sm"
                                                        onClick={() => {
                                                          formField.FeeAmounts?.splice(
                                                            kvpIdx,
                                                            1
                                                          );
                                                          setFieldValue(
                                                            `formFields[${formFieldIdx}].FeeAmounts`,
                                                            formField.FeeAmounts
                                                          );
                                                        }}
                                                      />
                                                    </Box>
                                                  </HStack>
                                                  <TextArea
                                                    horizontal
                                                    label={`Product Service Description ${
                                                      kvpIdx + 1
                                                    }`}
                                                    valueProps={{ flexGrow: 5 }}
                                                    name={`formFields[${formFieldIdx}].FeeAmounts[${kvpIdx}].value.ProductServiceDescription`}
                                                  />
                                                  {kvpIdx !=
                                                    formField.FeeAmounts!
                                                      .length -
                                                      1 && <Divider />}
                                                </VStack>
                                              );
                                            }
                                          )) || (
                                          <Text
                                            fontStyle="italic"
                                            fontSize="sm"
                                          >
                                            Fee Amounts has not been set.
                                          </Text>
                                        )}
                                      </VStack>
                                    </Box>
                                  )}

                                  {formField.Type == FieldType.Textbox && (
                                    <HStack w="100%" spacing={4}>
                                      <ChakraReactSelectField
                                        horizontal
                                        label="Field Validation Regex Type"
                                        name={`formFields[${formFieldIdx}].FieldValidationRegexType`}
                                        selectedOptionColor="blue"
                                        isLoading={
                                          fieldValidationRegexTypeOptionsLoading
                                        }
                                        options={
                                          fieldValidationRegexTypeOptions?.map(
                                            (item) => {
                                              return {
                                                label: item.Value!,
                                                value: item.Key!,
                                              };
                                            }
                                          ) || []
                                        }
                                        className="relative-listbox"
                                        fcProps={{ maxW: "50%" }}
                                        isDisabled={isExtendedField(
                                          formField.Identifier
                                        )}
                                      />
                                      {formField.FieldValidationRegexType ==
                                        "CustomRegex" && (
                                        <TextField
                                          horizontal
                                          label="Custom Validation Regex Pattern"
                                          name={`formFields[${formFieldIdx}].CustomValidationRegexPattern`}
                                          isDisabled={isExtendedField(
                                            formField.Identifier
                                          )}
                                        />
                                      )}
                                    </HStack>
                                  )}

                                  <Divider my={2} />

                                  <SwitchField
                                    horizontal
                                    labelProps={{
                                      flexShrink: 1,
                                      whiteSpace: "nowrap",
                                    }}
                                    valueProps={{ flexGrow: 5 }}
                                    label="Accept Third Party Data"
                                    name={`formFields[${formFieldIdx}].AcceptThirdPartyData`}
                                    colorScheme="green"
                                  />

                                  {formField.AcceptThirdPartyData && (
                                    <ThirdPartyDataConfigSection
                                      fieldName={`formFields[${formFieldIdx}].ThirdPartyDataFieldDataletConfigurations`}
                                      configurations={
                                        formField.ThirdPartyDataFieldDataletConfigurations
                                      }
                                      setFieldValue={setFieldValue}
                                      borderColor="cherryButtonDark.100"
                                    />
                                  )}
                                </VStack>
                              )}
                            </AccordionPanel>
                          </>
                        )}
                      </AccordionItem>
                    );
                })) || (
                <VStack w="100%" p={4} spacing={2}>
                  <Text fontStyle="italic" fontSize="sm">
                    Form fields not set
                  </Text>
                  <Button
                    size="sm"
                    leftIcon={<PlusIcon />}
                    onClick={() => addCustomFormField()}
                    colorScheme="cherryButton"
                  >
                    Form Field
                  </Button>
                </VStack>
              )}
            </SortableContext>
            <DragOverlay style={{ minWidth: "800px" }}>
              {activeId ? (
                <AccordionItem>
                  <HStack w="100%" pl={4}>
                    <SortableItem
                      id={
                        activeId.customField
                          ? getFormFieldId(activeId.customField)
                          : ""
                      }
                      whiteSpace="nowrap"
                    >
                      <AccordionButton
                        style={{
                          paddingLeft: "unset",
                          paddingRight: "unset",
                        }}
                        _focus={{ boxShadow: "unset" }}
                      >
                        {!activeId.customField?.IsVisible && (
                          <Tag size="sm" mr="2">
                            Hidden
                          </Tag>
                        )}
                        <Text>
                          <b>
                            {activeId.customField?.Name ||
                              `#${activeId.idx + 1} Custom Field`}
                          </b>{" "}
                          -{" "}
                          {
                            activeId.customField?.FieldSpecification
                              ?.SurrogateKey
                          }
                        </Text>
                      </AccordionButton>
                    </SortableItem>

                    {activeId.customField?.IsVisible && (
                      <IconButton
                        aria-label="Hide custom form field."
                        title="Hide custom form field."
                        icon={<ViewOffIcon />}
                        size="xs"
                        bgColor="cherryGrey.160"
                      />
                    )}
                    {!activeId.customField?.IsVisible && (
                      <IconButton
                        aria-label="Show custom form field."
                        title="Show custom form field."
                        icon={<ViewIcon />}
                        size="xs"
                        bgColor="cherryGrey.160"
                      />
                    )}
                    <IconButton
                      aria-label="Delete custom form field."
                      title="Delete custom form field."
                      icon={<DeleteIcon />}
                      size="xs"
                      bgColor="cherryGrey.160"
                    />
                    <AccordionButton
                      flexGrow={1}
                      justifyContent="flex-end"
                      _focus={{ boxShadow: "unset" }}
                    >
                      <AccordionIcon />
                    </AccordionButton>
                  </HStack>
                  <AccordionPanel width="100%"></AccordionPanel>
                </AccordionItem>
              ) : null}
            </DragOverlay>
          </VStack>
        </Accordion>
      </DndContext>
    </>
  );
};
